import {
  Accordion,
  Badge,
  Box,
  Card,
  Container,
  Group,
  List,
  rem,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import { PricingTable } from '@clerk/react';

import { ScaleLoader } from '@/components/atoms';
import { CONTACT_LIMIT_PROSPECT, MESSAGE_LIMIT_PROSPECT } from '@/types/permissions';

type PlanDetail = {
  name: string;
  summary: string;
  bullets: string[];
};

/**
 * Every bullet here must map to something the gate map actually enforces
 * (`FEATURE_MIN_PLAN` in `@/types/permissions`) - this copy sits directly under
 * Clerk's `<PricingTable />`, so a claim with no gate behind it reads as a
 * promise the product then breaks. The two Trace caps come from the same
 * constants the server enforces rather than being retyped here.
 */
const PLAN_DETAILS: PlanDetail[] = [
  {
    name: 'Trace',
    summary: 'Free forever while you find your feet.',
    bullets: [
      `Stay-in-touch reminders for up to ${CONTACT_LIMIT_PROSPECT} contacts`,
      `${MESSAGE_LIMIT_PROSPECT} AI check-in drafts a month, and the interaction log`,
      'CSV import so you can start immediately',
    ],
  },
  {
    name: 'Seam',
    summary: 'For people who genuinely work their network.',
    bullets: [
      'Unlimited contacts and unlimited check-ins',
      'Gmail import to find the people you actually email',
      'The full weekly retrospective, not just the headline',
    ],
  },
  {
    name: 'Vein',
    summary: 'For keeping Assay and your work CRM in step.',
    bullets: [
      'Everything in Seam',
      'HubSpot sync, both ways, for contacts and deals',
      'Check-ins logged back to the CRM timeline automatically',
    ],
  },
  {
    name: 'Nugget',
    summary: 'Hands-on support and deeper context.',
    bullets: [
      'Everything in Vein',
      'Context: upload notes, screenshots or exports and Assay reads them',
      'Deep enrichment on your key people, plus white-glove setup',
    ],
  },
];

const FAQS = [
  {
    question: 'What happens when I hit my contact limit?',
    answer: `Trace includes up to ${CONTACT_LIMIT_PROSPECT} contacts and ${MESSAGE_LIMIT_PROSPECT} check-ins each month. When you reach the cap, you can upgrade instantly to unlimited usage. Your data stays put - nothing gets deleted.`,
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. You can change or cancel your plan from the billing portal. If you cancel, you keep access until the end of your billing period and can export your data at any time.',
  },
  {
    question: 'Is my data safe?',
    answer:
      'Yes. We use enterprise-grade encryption and authentication. Only you can access your contacts and check-in history.',
  },
];

export function PricingSection() {
  return (
    <Box py={rem(80)}>
      <Container size="lg">
        <Stack gap="xl">
          <Stack align="center" gap="xs" ta="center">
            <Title order={1}>Simple, transparent pricing</Title>
            <Text size="sm" c="dimmed" maw={520}>
              Pick the plan that fits how big your world is.
            </Text>
          </Stack>

          <Card withBorder padding="lg" radius="md" data-testid="pricing-table">
            <PricingTable
              fallback={<ScaleLoader />}
              ctaPosition="bottom"
              collapseFeatures={false}
              newSubscriptionRedirectUrl="/app"
            />
          </Card>

          <Stack gap="md">
            <Title order={2}>What’s in each tier</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
              {PLAN_DETAILS.map((plan) => (
                <Card key={plan.name} withBorder padding="lg" radius="md">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <Badge color="ore" variant="light">
                        {plan.name}
                      </Badge>
                      <Text size="sm" c="dimmed">
                        {plan.summary}
                      </Text>
                    </Group>
                    <List spacing={6} size="sm" c="dimmed">
                      {plan.bullets.map((bullet) => (
                        <List.Item key={bullet}>{bullet}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>

          <Stack gap="sm">
            <Title order={2}>FAQs</Title>
            <Accordion variant="separated" radius="md">
              {FAQS.map((item) => (
                <Accordion.Item key={item.question} value={item.question}>
                  <Accordion.Control>{item.question}</Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" c="dimmed">
                      {item.answer}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
